import {TemperatureUnit} from 'puffco-api-axios-client';

import {Constants} from '../../../../../constants';
import {LightingPattern, isColorLight, isMoodLightDto} from '../../../../types';
import {meetsMinimumFirmware} from '../../../../utils';
import {Temperature} from '../../../../utils/temperature';
import {Scratchpad} from '../../Scratchpad';
import {
  HEAT_CYCLE_ARRAY_INDICES,
  isTempHeatProfileIndex,
} from '../../pikaparam';
import {getTarget} from '../../util';
import {IUnitClient} from '../UnitClient';
import {IUnitLedApi} from '../UnitLedApi';
import {
  HeatCycle,
  HeatCycleIndex,
  IUnitCycleAccess,
  ReadHeatCycle,
  ReadHeatCycleOptions,
} from './IUnitCycleAccess';

export class UnitCycleAccess implements IUnitCycleAccess {
  constructor(
    private softwareRevision: string,
    private client: IUnitClient,
    private leds: IUnitLedApi,
  ) {}

  public async readSelectedHeatCycle() {
    return this.client.read<number>('selectedHeatCycle');
  }

  public async writeSelectedHeatCycle(index: HeatCycleIndex) {
    await this.client.write<number>('selectedHeatCycle', index);
  }

  public async writeHeatCycles(cycles: HeatCycle[]) {
    await Promise.all(cycles.map(cycle => this.writeHeatCycle(cycle)));
  }

  public async readHeatCycles(
    options: ReadHeatCycleOptions,
  ): Promise<ReadHeatCycle[]> {
    return Promise.all(
      HEAT_CYCLE_ARRAY_INDICES.map(order => this.readHeatCycle(order, options)),
    );
  }

  public async writeTempHeatCycle(cycle: HeatCycle) {
    return this.writeHeatCycle({
      ...cycle,
      order: Constants.TEMP_HEAT_PROFILE_INDEX,
    });
  }

  public async writeHeatCycle(cycle: HeatCycle): Promise<void> {
    const order = cycle.order;
    const temperature = Temperature.convert(cycle.temperature, {
      from: cycle.units,
      to: TemperatureUnit.Celsius,
    });

    const base = isTempHeatProfileIndex(order)
      ? ('tempHeatCycle' as const)
      : (`heatCycle${order}` as const);

    if (!isTempHeatProfileIndex(order)) {
      await this.client.write<string>(`${base}Name`, cycle.name);
    }

    await this.client.write<number>(`${base}Temp`, temperature);

    await this.client.write<number>(`${base}Time`, cycle.duration);

    if (
      cycle.vaporSetting !== undefined &&
      meetsMinimumFirmware(
        this.softwareRevision,
        Constants.MINIMUM_FIRMWARE_VERSION.VAPOR_SETTING,
      )
    ) {
      await this.client.write<number>(`${base}Intensity`, cycle.vaporSetting);
    }

    const metadata = new Scratchpad({
      profile: cycle,
      ...(isMoodLightDto(cycle.light) && {moodLight: cycle.light}),
    });

    const target = getTarget(order);

    if (isMoodLightDto(cycle.light)) {
      await this.leds.writeLight({
        target,
        light: cycle.light,
        metadata,
      });

      return;
    }

    if (isColorLight(cycle.light)) {
      await this.leds.writeLight({
        light: {
          color: cycle.light.color,
          pattern: LightingPattern.STEADY,
        },
        target,
        metadata,
      });
    }
  }

  public async readHeatCycle(
    index: HeatCycleIndex,
    options: ReadHeatCycleOptions,
  ): Promise<ReadHeatCycle> {
    const target = isTempHeatProfileIndex(index)
      ? ('tempHeatCycle' as const)
      : (`heatCycle${index}` as const);

    const name = isTempHeatProfileIndex(index)
      ? undefined
      : await this.client.read<string>(`${target}Name`);

    const temperature = await this.client.read<number>(`${target}Temp`);

    const duration = await this.client.read<number>(`${target}Time`);

    const intensity = meetsMinimumFirmware(
      this.softwareRevision,
      Constants.MINIMUM_FIRMWARE_VERSION.VAPOR_SETTING,
    )
      ? await this.client.read<number>(`${target}Intensity`)
      : undefined;

    const {light, metadata} = await this.leds.readLight({target});

    const {profile} = metadata?.segregate() ?? {};

    return {
      id: profile?.id,
      name: name ?? '',
      order: index,
      modified: profile?.modified,
      temperature: Temperature.convert(temperature, {
        from: TemperatureUnit.Celsius,
        to: options.units,
      }),
      vaporSetting: intensity,
      duration,
      units: options.units,
      light,
    };
  }
}
